import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Clock, Globe, Zap, Users, Target, TrendingUp, Coffee, Lightbulb, Award, Heart } from "lucide-react";
import EnhancedSEO from "@/components/SEO/EnhancedSEO";
import { useToast } from "@/components/ui/use-toast";

const Culture = () => {
  const { toast } = useToast();

  const handleNumberClick = () => {
    toast({
      title: "Lock in your discovery call with us",
      description: "Use the AI agent to get information to see if you qualify",
      duration: 5000,
    });
  };
  const cultureValues = [
    {
      icon: Zap,
      title: "Move Fast, Think Smart",
      description: "We believe in rapid execution backed by strategic thinking. Speed without strategy is chaos; strategy without speed is paralysis."
    },
    {
      icon: Globe,
      title: "Global Mindset",
      description: "Our team operates across time zones, bringing diverse perspectives and 24/7 execution capability to every project."
    },
    {
      icon: Target,
      title: "Results-Driven",
      description: "We measure success by outcomes, not hours. Every action is tied to measurable business impact for our clients."
    },
    {
      icon: Lightbulb,
      title: "Creative Problem Solving",
      description: "We approach challenges with fresh perspectives, innovative solutions, and the courage to try new approaches."
    }
  ];

  const workingPrinciples = [
    {
      icon: Clock,
      title: "Flexible Hours, Fixed Results",
      description: "Work when you're most productive. We care about what you deliver, not when you deliver it."
    },
    {
      icon: Users,
      title: "Collaborative Excellence",
      description: "Every team member's voice matters. We build on each other's ideas to create exceptional outcomes."
    },
    {
      icon: TrendingUp,
      title: "Continuous Growth",
      description: "We invest in your development with mentorship, training, and real-world learning opportunities."
    },
    {
      icon: Award,
      title: "Merit-Based Recognition",
      description: "Your contributions are recognized and rewarded based on impact, innovation, and results."
    }
  ];

  const perks = [
    "100% Remote Work",
    "Flexible Schedule",
    "Professional Development Budget",
    "Latest Tools & Technology",
    "Performance Bonuses",
    "Mentorship Programs",
    "Creative Freedom",
    "Global Team Collaboration"
  ];

  return (
    <>
      <EnhancedSEO
        title="Culture - Vincent Growth Partners"
        description="Discover the culture at Vincent Growth Partners - a remote-first, results-driven marketing agency built by sharp young professionals who move fast and think creatively."
        keywords="company culture, remote work, marketing agency culture, young professionals, results-driven workplace"
        url="https://vincentgrowthpartners.com/culture"
      />
      
      <div className="min-h-screen">
        {/* Hero Section */}
        <section className="relative py-20" style={{ background: 'var(--gradient-hero)' }}>
          <div className="container mx-auto px-6">
            <div className="max-w-4xl mx-auto text-center text-white">
              <h1 className="text-5xl md:text-6xl font-bold mb-6 leading-tight">
                Our <span className="text-secondary">Culture</span>
              </h1>
              <div className="text-xl md:text-2xl mb-8 text-white/90 leading-relaxed">
                <p className="mb-4">Our Workplace Culture at Vincent Growth Partners</p>
                <p className="mb-4">At VGP, we don't clock-watch — we engineer results.</p>
                <p>We're a remote-first, results-driven marketing agency built by a team of sharp, young professionals who move fast, think creatively, and operate globally.</p>
              </div>
            </div>
          </div>
        </section>

        {/* Culture Values */}
        <section className="py-20 bg-background">
          <div className="container mx-auto px-6">
            <div className="max-w-6xl mx-auto">
              <h2 className="text-4xl md:text-5xl font-bold mb-12 text-center text-foreground">
                Our Core <span className="text-secondary">Values</span>
              </h2>
              
              <div className="grid lg:grid-cols-2 gap-8">
                {cultureValues.map((value, index) => (
                  <Card key={index} className="border-2 border-transparent hover:border-secondary/20 transition-all duration-300 shadow-elegant">
                    <CardContent className="p-8">
                      <div className="flex items-start space-x-4">
                        <div className="w-12 h-12 bg-gradient-to-br from-primary to-primary-light rounded-lg flex items-center justify-center flex-shrink-0">
                          <value.icon className="w-6 h-6 text-secondary" />
                        </div>
                        <div className="flex-1">
                          <h3 className="text-xl font-bold text-foreground mb-3">{value.title}</h3>
                          <p className="text-muted-foreground leading-relaxed">{value.description}</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Working Principles */}
        <section className="py-20 bg-muted/50">
          <div className="container mx-auto px-6">
            <div className="max-w-6xl mx-auto">
              <h2 className="text-4xl md:text-5xl font-bold mb-12 text-center text-foreground">
                How We <span className="text-secondary">Work</span>
              </h2>
              
              <div className="grid md:grid-cols-2 gap-8">
                {workingPrinciples.map((principle, index) => (
                  <Card key={index} className="border-0 shadow-elegant hover:shadow-2xl transition-all duration-300">
                    <CardContent className="p-8">
                      <div className="flex items-start space-x-4">
                        <div className="w-12 h-12 bg-gradient-to-br from-primary to-primary-light rounded-lg flex items-center justify-center flex-shrink-0">
                          <principle.icon className="w-6 h-6 text-secondary" />
                        </div>
                        <div className="flex-1">
                          <h3 className="text-xl font-bold text-foreground mb-3">{principle.title}</h3>
                          <p className="text-muted-foreground leading-relaxed">{principle.description}</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Team Environment */}
        <section className="py-20 bg-background">
          <div className="container mx-auto px-6">
            <div className="max-w-4xl mx-auto text-center">
              <h2 className="text-4xl md:text-5xl font-bold mb-6 text-foreground">
                Why Join <span className="text-secondary">Our Team?</span>
              </h2>
              <p className="text-xl text-muted-foreground mb-12 leading-relaxed">
                We're not just building a company — we're crafting careers and creating opportunities 
                for the next generation of marketing professionals.
              </p>
              
              <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
                {perks.map((perk, index) => (
                  <Card key={index} className="border-0 shadow-elegant">
                    <CardContent className="p-6 text-center">
                      <Heart className="w-8 h-8 text-secondary mx-auto mb-3" />
                      <p className="font-medium text-foreground">{perk}</p>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Team Stats */}
        <section className="py-20 bg-muted/50">
          <div className="container mx-auto px-6">
            <div className="max-w-4xl mx-auto text-center">
              <h2 className="text-4xl md:text-5xl font-bold mb-12 text-foreground">
                Our Team <span className="text-secondary">By Numbers</span>
              </h2>
              
              <div className="grid md:grid-cols-3 gap-8">
                <Card className="border-0 shadow-elegant hover:shadow-2xl transition-all duration-300 cursor-pointer" onClick={handleNumberClick}>
                  <CardContent className="p-8 text-center">
                    <div className="text-4xl font-bold text-secondary mb-2 hover:scale-105 transition-transform duration-300">100%</div>
                    <p className="text-muted-foreground">Remote Team</p>
                  </CardContent>
                </Card>
                <Card className="border-0 shadow-elegant hover:shadow-2xl transition-all duration-300 cursor-pointer" onClick={handleNumberClick}>
                  <CardContent className="p-8 text-center">
                    <div className="text-4xl font-bold text-secondary mb-2 hover:scale-105 transition-transform duration-300">24/7</div>
                    <p className="text-muted-foreground">Global Coverage</p>
                  </CardContent>
                </Card>
                <Card className="border-0 shadow-elegant hover:shadow-2xl transition-all duration-300 cursor-pointer" onClick={handleNumberClick}>
                  <CardContent className="p-8 text-center">
                    <div className="text-4xl font-bold text-secondary mb-2 hover:scale-105 transition-transform duration-300">Young</div>
                    <p className="text-muted-foreground">& Ambitious</p>
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 bg-gradient-to-r from-primary to-primary-light">
          <div className="container mx-auto px-6 text-center">
            <div className="max-w-4xl mx-auto text-white">
              <h2 className="text-4xl md:text-5xl font-bold mb-6">
                Ready to Join Our Culture?
              </h2>
              <p className="text-xl mb-8 text-white/90">
                Whether you're looking to partner with us or join our team, 
                experience the VGP difference firsthand.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button variant="premium" size="xl" asChild>
                  <a href="/do-you-qualify">
                    Partner With Us
                  </a>
                </Button>
                <Button variant="premium" size="xl" asChild>
                  <a href="https://www.linkedin.com/hiring/jobs/4303520807/detail/" target="_blank" rel="noopener noreferrer">
                    View Careers
                  </a>
                </Button>
              </div>
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default Culture;